import { useState } from 'react';
import Modal from './Modal';
import { useDesign } from '../utils/appearance';
import { useConfirm, useToast } from './ui';

// Bestätigungen, die etwas entfernen/zurücksetzen, bekommen im neuen Design einen roten Knopf
const DANGER_PATTERN = /lösch|entfern|leer|deaktiv|ablehn|zurücksetz|widerruf|verwerf/i;

// Hook für einfache Modals
export function useModal() {
  const [modalState, setModalState] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
    onConfirm: null,
    onCancel: null,
    confirmText: 'Bestätigen',
    cancelText: 'Abbrechen'
  });

  const showModal = ({ title, message, type = 'info', onConfirm, onCancel, confirmText, cancelText }) => {
    setModalState({
      isOpen: true,
      title,
      message,
      type,
      onConfirm,
      onCancel,
      confirmText: confirmText || 'Bestätigen',
      cancelText: cancelText || 'Abbrechen'
    });
  };

  const hideModal = () => {
    setModalState(prev => ({ ...prev, isOpen: false }));
  };

  const handleConfirm = () => {
    if (modalState.onConfirm) {
      modalState.onConfirm();
    }
    hideModal();
  };

  const handleCancel = () => {
    if (modalState.onCancel) {
      modalState.onCancel();
    }
    hideModal();
  };

  const ModalComponent = () => (
    <Modal
      isOpen={modalState.isOpen}
      onClose={hideModal}
      title={modalState.title}
      type={modalState.type}
      actions={
        modalState.type === 'confirm' ? (
          <>
            <button
              onClick={handleCancel}
              className="modal-button modal-button-secondary"
            >
              {modalState.cancelText}
            </button>
            <button
              onClick={handleConfirm}
              className="modal-button modal-button-primary"
            >
              {modalState.confirmText}
            </button>
          </>
        ) : (
          <button
            onClick={handleConfirm}
            className="modal-button modal-button-primary"
          >
            OK
          </button>
        )
      }
    >
      {modalState.message}
    </Modal>
  );

  // Neues Design: Erfolg/Info als Toast, Rückfragen als neuer Bestätigungsdialog.
  // Fehler und Warnungen bleiben ein Dialog, den man bewusst bestätigt.
  const modern = useDesign() === 'modern';
  const toast = useToast();
  const confirm = useConfirm();
  if (modern) {
    return {
      showSuccess: (title, message) => { toast.success(message, { title, duration: 6000 }); return Promise.resolve(); },
      showInfo: (title, message) => { toast.info(message, { title }); return Promise.resolve(); },
      showError: (title, message) => new Promise(resolve => showModal({ title, message, type: 'error', onConfirm: resolve, onCancel: resolve })),
      showWarning: (title, message) => new Promise(resolve => showModal({ title, message, type: 'warning', onConfirm: resolve, onCancel: resolve })),
      showConfirm: (title, message, confirmText, cancelText) => confirm({
        title,
        message,
        confirmLabel: confirmText,
        cancelLabel: cancelText,
        danger: DANGER_PATTERN.test(`${confirmText || ''} ${title || ''}`)
      }),
      ModalComponent
    };
  }

  return {
    showSuccess: (title, message) => new Promise(resolve => showModal({ title, message, type: 'success', onConfirm: resolve, onCancel: resolve })),
    showError: (title, message) => new Promise(resolve => showModal({ title, message, type: 'error', onConfirm: resolve, onCancel: resolve })),
    showWarning: (title, message) => new Promise(resolve => showModal({ title, message, type: 'warning', onConfirm: resolve, onCancel: resolve })),
    showInfo: (title, message) => new Promise(resolve => showModal({ title, message, type: 'info', onConfirm: resolve, onCancel: resolve })),
    showConfirm: (title, message, confirmText, cancelText) => {
      return new Promise((resolve) => {
        showModal({ 
          title, 
          message, 
          type: 'confirm', 
          confirmText,
          cancelText,
          onConfirm: () => resolve(true),
          onCancel: () => resolve(false)
        });
      });
    },
    ModalComponent
  };
}
